import { useQuery } from "@apollo/client";
import { useTheme } from "styled-components";
import { FiAward } from "react-icons/fi";

import { Avatar } from "MVVM/Components/AvatarV2";
import { GET_FRIENDS_LEADERBOARD } from "MVVM/GraphQL/homepage";
import { useUser } from "MVVM/Hooks/useUser";
import { trackFeedClick } from "../../analytics";
import {
  WidgetCard,
  WidgetHeader,
  WidgetHeaderAction,
  WidgetTitle,
} from "../styledComponents";
import { List, NameLink, Rank, Row, Score, YouLabel } from "./styledComponents";

interface IFriendsLeaderboardEntry {
  rank: number;
  score: number;
  user: {
    id: string;
    username: string;
    avatarUrl: string | null;
  };
}

const LEADERBOARD_LIMIT = 5;

const rankColor = (
  rank: number,
  theme: { colors: Record<string, string> },
): string | undefined => {
  if (rank === 1) return theme.colors.goldStar ?? "#ffd700";
  if (rank === 2) return theme.colors.chalk;
  if (rank === 3) return theme.colors.rare;
  return undefined;
};

export const FriendsLeaderboardWidget = () => {
  const theme = useTheme() as { colors: Record<string, string> };
  const { allstarUser } = useUser();
  const viewerId = allstarUser?.user?._id;

  const { data, loading, error } = useQuery(GET_FRIENDS_LEADERBOARD, {
    variables: { limit: LEADERBOARD_LIMIT },
    fetchPolicy: "cache-and-network",
  });

  const entries: IFriendsLeaderboardEntry[] =
    data?.me?.friendsLeaderboard ?? [];

  if (loading || error || entries.length === 0) return null;

  return (
    <WidgetCard>
      <WidgetHeader>
        <WidgetTitle>
          <FiAward size={15} />
          Friends leaderboard
        </WidgetTitle>
        <WidgetHeaderAction>View all</WidgetHeaderAction>
      </WidgetHeader>

      <List>
        {entries.map((entry) => {
          const isYou = entry.user.id === viewerId;
          const color = rankColor(entry.rank, theme);
          return (
            <Row key={entry.user.id} $highlighted={isYou}>
              <Rank $color={color}>#{entry.rank}</Rank>
              <Avatar size="xs" src={entry.user.avatarUrl} />
              <NameLink
                to={`/u/${entry.user.username}`}
                $color={color}
                onClick={() =>
                  trackFeedClick("profile", { username: entry.user.username })
                }
              >
                {entry.user.username}
                {isYou && <YouLabel> (you)</YouLabel>}
              </NameLink>
              <Score>{Math.round(entry.score).toLocaleString()}</Score>
            </Row>
          );
        })}
      </List>
    </WidgetCard>
  );
};
